import { z } from 'zod'

export const PullRequestChecksSchema = z.object({
  status: z.enum(['success', 'failure', 'pending', 'unknown']),
  state: z.enum(['open', 'closed', 'merged']).optional(),
})
export type PullRequestChecks = z.infer<typeof PullRequestChecksSchema>

export const PullRequestChecksQuerySchema = z.object({
  project_id: z.string().min(1),
  repo: z.string().regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/),
  pr: z.coerce.number().int().positive(),
})

export const PullRequestCheckRevisionSchema = z.object({
  head: z.object({ sha: z.string().min(1) }),
  state: z.enum(['open', 'closed']),
  merged: z.boolean(),
  merge_commit_sha: z.string().nullable(),
})
export const PullRequestCheckRunsSchema = z.object({
  total_count: z.number().int().nonnegative(),
  check_runs: z.array(z.object({ status: z.string(), conclusion: z.string().nullable() })),
})
export const PullRequestCommitStatusesSchema = z.object({
  total_count: z.number().int().nonnegative(),
  statuses: z.array(z.object({ state: z.string() })),
})

export function summarizePullRequestChecks(
  checks: z.infer<typeof PullRequestCheckRunsSchema>['check_runs'],
  statuses: z.infer<typeof PullRequestCommitStatusesSchema>['statuses'],
): PullRequestChecks {
  const successful = new Set(['success', 'neutral', 'skipped'])
  const failed = new Set(['failure', 'cancelled', 'timed_out', 'action_required', 'startup_failure', 'stale'])
  if (checks.some(check => check.status === 'completed' && failed.has(check.conclusion ?? ''))
    || statuses.some(status => status.state === 'failure' || status.state === 'error')) return { status: 'failure' }
  if (checks.some(check => check.status !== 'completed') || statuses.some(status => status.state === 'pending')) return { status: 'pending' }
  if (checks.length + statuses.length === 0
    || checks.some(check => !successful.has(check.conclusion ?? ''))
    || statuses.some(status => status.state !== 'success')) return { status: 'unknown' }
  return { status: 'success' }
}
